/**
 * Translates between the semantic edge style (`EdgeStyle`: color / stroke_style /
 * stroke_width / arrows / curve_type) and React Flow's native visual props
 * (`style`, `markerStart`, `markerEnd`). Native fields are the source of truth;
 * `data` carries only `curve_type`.
 *
 * - `applyEdgeStyle(edge)` — semantic (from `edge.data`) → native fields. Used at
 *   write time when creating/editing an edge.
 * - `readEdgeStyle(edge)` — native fields → semantic. Used by the edge toolbar to
 *   read current state (works for agent- and client-created edges alike).
 * - `connectionLineTypeFromCurve` — the in-progress connection line while dragging.
 */

import { ConnectionLineType, Edge, MarkerType } from "@xyflow/react";
import type { ConnectorCurveType } from "../nodes/ConnectorFloatingToolbar";
import type { EdgeStyle } from "../nodes/EdgeFloatingToolbar";
import type { ArrowEndStyle } from "../nodes/shared/ArrowEndPicker";
import { DEFAULT_EDGE_COLOR } from "../nodes/shared/CanvasColorPalettes";

/** Maps connector curve preset to React Flow's in-progress connection line. */
export function connectionLineTypeFromCurve(
  curveType: ConnectorCurveType,
): ConnectionLineType {
  if (curveType === "smooth") return ConnectionLineType.Bezier;
  if (curveType === "straight") return ConnectionLineType.Straight;
  return ConnectionLineType.SmoothStep;
}

/** Restore RF visual props (style, markers) from persisted edge data. */
export function applyEdgeStyle(edge: Edge): Edge {
  const data = (edge.data ?? {}) as EdgeStyle;
  // "default" is a legacy sentinel — treat it the same as missing.
  const color =
    data.color && data.color !== "default" ? data.color : DEFAULT_EDGE_COLOR;
  const strokeStyle = data.stroke_style ?? "solid";
  const strokeWidth = data.stroke_width ?? 2;
  const strokeDasharray =
    strokeStyle === "dashed"
      ? "16 8"
      : strokeStyle === "dotted"
        ? "3 8"
        : undefined;

  // Migrate legacy arrowMode → startArrow/endArrow
  const legacyArrow = data.arrow_mode;
  const endArrow =
    data.end_arrow ?? (legacyArrow === "none" ? "none" : "arrow");
  const startArrow =
    data.start_arrow ?? (legacyArrow === "both" ? "arrow" : "none");

  const markerForStyle = (style: string) => {
    if (style === "none") return undefined;
    if (style === "arrow")
      return { type: MarkerType.ArrowClosed, color, width: 16, height: 16 };
    if (style === "arrow-open")
      return { type: MarkerType.Arrow, color, width: 16, height: 16 };
    return undefined;
  };

  return {
    ...edge,
    type: edge.type ?? "labeled",
    style: { strokeWidth, stroke: color, strokeDasharray },
    markerEnd: markerForStyle(endArrow),
    markerStart: markerForStyle(startArrow),
  };
}

/** Native RF visual props → semantic `EdgeStyle` (inverse of `applyEdgeStyle`). */
export function readEdgeStyle(edge: Edge): EdgeStyle {
  const style = (edge.style ?? {}) as {
    stroke?: string;
    strokeWidth?: number;
    strokeDasharray?: string;
  };
  const data = (edge.data ?? {}) as { curve_type?: ConnectorCurveType };

  const arrowFromMarker = (marker: Edge["markerStart"]): ArrowEndStyle => {
    if (!marker || typeof marker === "string") return "none";
    if (marker.type === MarkerType.ArrowClosed) return "arrow";
    if (marker.type === MarkerType.Arrow) return "arrow-open";
    return "none";
  };

  const strokeStyle =
    style.strokeDasharray === "16 8"
      ? "dashed"
      : style.strokeDasharray === "3 8"
        ? "dotted"
        : "solid";

  return {
    color: style.stroke ?? "default",
    stroke_width: style.strokeWidth ?? 2,
    stroke_style: strokeStyle,
    start_arrow: arrowFromMarker(edge.markerStart),
    end_arrow: arrowFromMarker(edge.markerEnd),
    curve_type: data.curve_type ?? "step",
  };
}
